/**
 * InterviewTab Component
 * Reusable tab component for displaying AI interviews in Resume and Job detail pages.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { aiInterviewsApi } from '../../services/api'
import InterviewListView from './InterviewListView'
import InterviewQuestionsPreviewModal from './InterviewQuestionsPreviewModal'
import { useInterviewPolling } from '../../hooks/useInterviewPolling'
import type { AIInterview, AIInterviewListResponse, AIInterviewQuestion } from '../../types/interview'

interface InterviewTabProps {
  entityType: 'resume' | 'job'
  entityId: string
  candidateName?: string
  jobTitle?: string
  isAdmin?: boolean
  // Added 2026-10-07 — gates InterviewListView's Preview & Edit Questions,
  // Retry Processing, and Reanalyze (interviews.generate), and Start
  // Interview (interviews.create). Both default false, same as isAdmin,
  // so an older caller that doesn't pass them gets the safe/hidden state
  // rather than accidentally staying open.
  canManageContent?: boolean
  canCreateInterview?: boolean
  isActive?: boolean // When true, refetch data (for tab activation)
}

export default function InterviewTab({
  entityType,
  entityId,
  candidateName,
  jobTitle,
  isAdmin = false,
  canManageContent = false,
  canCreateInterview = false,
  isActive = false,
}: InterviewTabProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedInterview, setSelectedInterview] = useState<AIInterview | null>(null)

  // Fetch interviews based on entity type
  const {
    data: interviewsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ai-interviews', entityType, entityId, page, statusFilter],
    queryFn: async () => {
      const params = {
        page,
        page_size: 10,
        status: statusFilter,
      }

      if (entityType === 'resume') {
        const response = await aiInterviewsApi.listByResume(entityId, params)
        return response.data as AIInterviewListResponse
      } else {
        const response = await aiInterviewsApi.listByJob(entityId, params)
        return response.data as AIInterviewListResponse
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always', // Always refetch when tab becomes active
    staleTime: 0, // Consider data always stale to ensure fresh data
  })

  // Refetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      refetch()
    }
  }, [isActive, refetch])

  const handleStartInterview = (interview: AIInterview) => {
    // Navigate to interview recording page, passing origin so we redirect back correctly after upload
    navigate(`/interviews/${interview.id}/record?origin=${entityType}`)
  }

  const handleDelete = async (interview: AIInterview) => {
    try {
      await aiInterviewsApi.delete(interview.id)
      toast.success(`Interview ${interview.session_id} deleted successfully`)
      refetch()
    } catch (error: any) {
      console.error('Failed to delete interview:', error)
      const message = error?.response?.data?.detail || 'Failed to delete interview'
      toast.error(message)
    }
  }

  const handlePreviewQuestions = (interview: AIInterview) => {
    setSelectedInterview(interview)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setSelectedInterview(null)
    // Refetch to get updated questions if user made changes
    refetch()
  }

  const interviews = interviewsData?.interviews || []
  const total = interviewsData?.total || 0
  const totalPages = interviewsData?.total_pages || 1

  // Poll every 5 s while any interview is in a transitional (queue-processing) state.
  // Stops automatically once all interviews reach a stable status.
  const isPolling = useInterviewPolling(interviews, refetch)

  return (
    <div className="space-y-3">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            AI Interviews
            {isPolling && (
              <span className="inline-flex items-center gap-1.5 text-xs font-normal text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                Processing…
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500">
            {entityType === 'resume'
              ? `Interviews for ${candidateName || 'this candidate'}`
              : `Interviews for ${jobTitle || 'this position'}`}
          </p>
        </div>

        {/* Status Filter */}
        <div className="flex items-center space-x-2">
          <label htmlFor="status-filter" className="text-xs font-medium text-gray-700">
            Status:
          </label>
          <select
            id="status-filter"
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter(e.target.value || undefined)
              setPage(1)
            }}
            className="block w-36 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="questions_generated">Questions Ready</option>
            <option value="in_progress">In Progress</option>
            <option value="audio_uploaded">Audio Uploaded</option>
            <option value="transcribed">Transcribed</option>
            <option value="analyzed">Analyzed</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-red-600">
            Failed to load interviews. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="mt-1.5 text-xs text-red-700 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Interview List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <InterviewListView
          interviews={interviews}
          isLoading={isLoading}
          onStartInterview={handleStartInterview}
          onPreviewQuestions={handlePreviewQuestions}
          onDelete={handleDelete}
          onReanalyzeSuccess={() => refetch()}
          isAdmin={isAdmin}
          canManageContent={canManageContent}
          canCreateInterview={canCreateInterview}
          emptyMessage={
            entityType === 'resume'
              ? 'No interviews have been conducted for this candidate yet. Create one from the matching results.'
              : 'No interviews have been conducted for this position yet.'
          }
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-gray-700">
                Showing <span className="font-medium">{(page - 1) * 10 + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * 10, total)}</span> of{' '}
                <span className="font-medium">{total}</span> interviews
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-1.5 py-1.5 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`relative inline-flex items-center px-3 py-1.5 border text-xs font-medium ${
                        page === pageNum
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center px-1.5 py-1.5 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Info text */}
      <p className="text-xs text-gray-500 text-center">
        {/* AI Interviews are generated when matching score is 40% or higher. */}
        Start from the {entityType === 'resume' ? 'Job Matching' : 'Apply Matching'} tab to create new interviews.
      </p>

      {/* Preview Questions Modal */}
      {showPreviewModal && selectedInterview && (
        <InterviewQuestionsPreviewModal
          isOpen={showPreviewModal}
          onClose={handleClosePreviewModal}
          interview={selectedInterview}
          questions={(selectedInterview.generated_questions as AIInterviewQuestion[]) || []}
          isGeneratingQuestions={false}
          candidateName={selectedInterview.candidate_name}
          jobTitle={selectedInterview.job_title}
          origin={entityType}
        />
      )}
    </div>
  )
}
